import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { setSessionToken } from "../hooks/useSession";
import { redirectAfterPasswordAuth } from "../lib/storePasswordCredential.js";
import { Button } from "./ui/Button.js";
import { Card } from "./ui/Card.js";
import { FieldError, Input, Label } from "./ui/Input.js";
import { AuthScaffold } from "./auth/AuthScaffold.js";
import { useDocumentTitle } from "../hooks/useDocumentTitle.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";
const MIN_PASSWORD_LENGTH = 8;

interface ReferralPreview {
  code: string;
  ownerDisplayName: string;
  commissionLevel: string | null;
  commissionLevelLocked: boolean;
  organization?: {
    brandName: string;
  } | null;
}

export function ReferralSignupPage() {
  useDocumentTitle("Join");
  const { referralCode } = useParams<{ referralCode: string }>();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<ReferralPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!referralCode) {
      navigate("/", { replace: true });
      return;
    }
    const code = referralCode;

    let cancelled = false;
    async function validateReferral() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_BASE}/api/referral-codes/validate/${encodeURIComponent(code)}`
        );
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.error || "This referral link is invalid or has expired.");
        }
        if (!cancelled) setPreview(json.data as ReferralPreview);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not validate referral link.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void validateReferral();
    return () => {
      cancelled = true;
    };
  }, [navigate, referralCode]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!referralCode || !preview) return;
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== passwordConfirmation) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referralCode, firstName, lastName, email, phone, password }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "Could not create your account.");
      }
      setSessionToken(json.data.sessionToken);
      await redirectAfterPasswordAuth(email, password, "/my-agent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create your account.");
      setSubmitting(false);
    }
  }

  return (
    <AuthScaffold blankPanel brandName={preview?.organization?.brandName ?? "Altura"}>
      <motion.main
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full space-y-6 py-6"
      >
        <Link to="/" className="focus-ring inline-flex rounded text-sm font-bold text-content-muted transition-colors hover:text-content-primary">
          ← Back home
        </Link>

        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-accent">Agent portal invitation</p>
          <h1 className="font-display text-4xl font-extrabold tracking-[-0.04em] text-content-primary">Make it official.</h1>
          <p className="leading-relaxed text-content-secondary">
            Set up your portal to track your contracts and build your downline.
          </p>
        </div>

        {loading ? (
          <Card className="space-y-3" role="status" aria-busy="true">
            <span className="sr-only">Validating referral invitation…</span>
            <div className="h-4 w-32 animate-pulse rounded bg-surface-muted" />
            <div className="h-5 w-56 animate-pulse rounded bg-surface-muted" />
          </Card>
        ) : preview ? (
          <Card variant="tinted" className="space-y-3 border-l-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-content-muted">You&apos;re joining</p>
              <p className="text-base font-semibold text-content-primary">
                {preview.ownerDisplayName} has invited you to join the {preview.organization?.brandName ?? "Altura"} organization
              </p>
            </div>
            {preview.commissionLevel && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-content-muted">Commission level</p>
                <p className="text-base font-semibold text-content-primary">{preview.commissionLevel}</p>
              </div>
            )}
          </Card>
        ) : (
          <Card className="space-y-3">
            <h2 className="font-display text-lg font-semibold text-content-primary">Invalid invitation</h2>
            <p className="text-sm text-content-secondary">
              {error || "This referral link is invalid or has expired."}
            </p>
            <Button variant="secondary" onClick={() => navigate("/")}>Return home</Button>
          </Card>
        )}

        {preview && (
          <Card padding="lg" variant="elevated" className="rounded-[12px]">
            <form className="space-y-4" onSubmit={handleSubmit}>
              {error && <FieldError role="alert">{error}</FieldError>}
              <div className="grid gap-4 sm:grid-cols-2">
                <Label>
                  First name
                  <Input
                    required
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    className="mt-1"
                  />
                </Label>
                <Label>
                  Last name
                  <Input
                    required
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    className="mt-1"
                  />
                </Label>
              </div>
              <Label>
                Email
                <Input
                  type="email"
                  name="username"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className="mt-1"
                />
              </Label>
              <Label>
                Phone
                <Input
                  type="tel"
                  name="tel"
                  required
                  autoComplete="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  placeholder="(555) 555-5555"
                  className="mt-1"
                />
              </Label>
              <Label>
                Password
                <Input
                  type="password"
                  name="new-password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-1"
                />
              </Label>
              <Label>
                Confirm password
                <Input
                  type="password"
                  name="new-password-confirm"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                  value={passwordConfirmation}
                  onChange={(event) => setPasswordConfirmation(event.target.value)}
                  className="mt-1"
                />
              </Label>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Creating account..." : "Create agent portal"}
              </Button>
            </form>
          </Card>
        )}

        <p className="text-center text-sm text-content-muted">
          Already have an account? <Link to="/login" className="font-bold text-accent hover:underline">Sign in</Link>
        </p>
      </motion.main>
    </AuthScaffold>
  );
}
